import type {
  FlowDefinition,
  FlowItemContextValue,
  FlowNode,
  FlowSelectionContextValue,
  PageFlowState,
  RequestItem,
  SelectedVase,
} from '../types';
import { vases } from '../data/vases';
import type { OrderItem } from '../types';
import { REQUEST_FLOW_FIELD_IDS } from './flowFieldExtensions';
import {
  getAddToListItemCount,
  getAddToListItemSocketId,
  getCompareOperatorsForValueType,
  getFlowNodeDefinition,
  getSelectionCollectorMode,
  getSelectionCollectorInputCount,
  getSelectionCollectorInputSocketId,
  getSelectableSourceFieldIds,
  resolveFlowNodeSockets,
  resolveFlowNodeSocketType,
  type CompareOperator,
} from './flowNodeDefinitions';
import {
  appendRequestItem,
  asItemContext,
  asRequestItem,
  asRequestItemList,
  asSelectionContext,
  buildItemContextFromSelection,
  buildRequestItemFromSelection,
  buildRequestItemFromSelectionContext,
  buildSelectionContext,
  clearRequestItemList,
  removeRequestItem,
} from './flowNodeListHelpers';
import { evaluateCompare, evaluateCondition } from './flowNodeLogicHelpers';
import { VASE_FLOW_FIELD_IDS } from './flows';
import {
  findPortalReceiversForSender,
} from './globalPortalTargets';
import { setStepTrackerActiveStepInState } from './progressContent';

const RUNTIME_META_PREFIX = '__runtime__';

function getFlowEntryValues(state: PageFlowState, flowId: string) {
  return state[flowId]?.values ?? {};
}

function valuesEqual(left: unknown, right: unknown) {
  return JSON.stringify(left) === JSON.stringify(right);
}

function getBindingFieldId(
  node: FlowNode,
  direction: 'input' | 'output',
  socketId: string
) {
  const bindings = direction === 'input' ? node.inputs : node.outputs;
  return bindings.find((binding) => binding.socketId === socketId)?.fieldId ?? null;
}

export function getFlowNodeRuntimeMetaKey(nodeId: string, suffix: string) {
  return `${RUNTIME_META_PREFIX}.${nodeId}.${suffix}`;
}

function getRuntimeMetaKey(nodeId: string, suffix: string) {
  return getFlowNodeRuntimeMetaKey(nodeId, suffix);
}

function getRuntimeMetaNumber(values: Record<string, unknown>, key: string) {
  return typeof values[key] === 'number' ? Number(values[key]) : null;
}

function getRuntimeMetaString(values: Record<string, unknown>, key: string) {
  return typeof values[key] === 'string' && values[key].trim().length > 0
    ? String(values[key])
    : null;
}

function resolveRemovedItemId(value: unknown) {
  if (typeof value === 'string' && value.trim().length > 0) return value.trim();
  if (
    value &&
    typeof value === 'object' &&
    typeof (value as { id?: unknown }).id === 'string' &&
    (value as { id: string }).id.trim().length > 0
  ) {
    return (value as { id: string }).id.trim();
  }
  return null;
}

function removeRequestItemById(listValue: unknown, itemIdValue: unknown) {
  const itemId = resolveRemovedItemId(itemIdValue);
  const list = asRequestItemList(listValue);
  if (!itemId) return list;
  return list.filter((item) => item.id !== itemId);
}

function syncCollectorStateToList(
  flow: FlowDefinition,
  values: Record<string, unknown>,
  requestItemsFieldId: string | null,
  updatedList: RequestItem[],
  patch: Record<string, unknown>
) {
  if (!requestItemsFieldId) return;

  flow.nodes.forEach((candidate) => {
    if (candidate.blockType !== 'selection-collector') return;
    const collectorItemsFieldId = getSmartBindingFieldId(flow, candidate, 'output', 'items');
    if (collectorItemsFieldId !== requestItemsFieldId) return;

    patch[getRuntimeMetaKey(candidate.id, 'collectedItems')] = updatedList;

    const currentSelection = asSelectionContext(values[getRuntimeMetaKey(candidate.id, 'collectedSelection')]);
    if (!currentSelection?.productId) {
      if (updatedList.length === 0) {
        patch[getRuntimeMetaKey(candidate.id, 'collectedSelection')] = null;
        patch[getRuntimeMetaKey(candidate.id, 'lastSource')] = null;
      }
      return;
    }

    const stillPresent = updatedList.some((item) => item.product?.id === currentSelection.productId);
    if (!stillPresent) {
      patch[getRuntimeMetaKey(candidate.id, 'collectedSelection')] = null;
      if (updatedList.length === 0) {
        patch[getRuntimeMetaKey(candidate.id, 'lastSource')] = null;
      }
    }
  });
}

function getPrimaryRequestItemsFieldId(flow: FlowDefinition) {
  return flow.fields.some((field) => field.id === REQUEST_FLOW_FIELD_IDS.requestItems)
    ? REQUEST_FLOW_FIELD_IDS.requestItems
    : null;
}

export function resolveRequestItemsFieldId(
  flow: FlowDefinition | null | undefined,
  args?: { nodeBlockId?: string | null; nodeId?: string | null }
) {
  if (!flow) return null;

  const targetNode =
    (args?.nodeId
      ? flow.nodes.find((entry) => entry.id === args.nodeId)
      : null) ??
    (args?.nodeBlockId
      ? flow.nodes.find(
          (entry) => entry.blockType === 'request-form' && entry.blockId === args.nodeBlockId
        )
      : null);

  if (targetNode?.blockType === 'request-form') {
    const explicitFieldId = getBindingFieldId(targetNode, 'input', 'requestItems');
    if (explicitFieldId) return explicitFieldId;
  }

  return getPrimaryRequestItemsFieldId(flow);
}

function getSmartBindingFieldId(
  flow: FlowDefinition,
  node: FlowNode,
  direction: 'input' | 'output',
  socketId: string
) {
  const explicitFieldId = getBindingFieldId(node, direction, socketId);
  if (explicitFieldId) return explicitFieldId;

  const requestItemsFieldId = resolveRequestItemsFieldId(flow, {
    nodeId: node.blockType === 'request-form' ? node.id : null,
  });
  if (!requestItemsFieldId) return null;

  if (
    node.blockType === 'request-form' &&
    direction === 'input' &&
    socketId === 'requestItems'
  ) {
    return requestItemsFieldId;
  }

  if (
    (node.blockType === 'add-to-list' ||
      node.blockType === 'remove-from-list' ||
      node.blockType === 'clear-list') &&
    socketId === 'list'
  ) {
    return requestItemsFieldId;
  }

  if (
    (node.blockType === 'add-to-list' ||
      node.blockType === 'remove-from-list' ||
      node.blockType === 'clear-list') &&
    direction === 'output' &&
    socketId === 'updatedList'
  ) {
    return requestItemsFieldId;
  }

  return null;
}

function getRawSocketValue(
  flow: FlowDefinition,
  values: Record<string, unknown>,
  node: FlowNode,
  direction: 'input' | 'output',
  socketId: string
) {
  const fieldId = getSmartBindingFieldId(flow, node, direction, socketId);
  return fieldId ? values[fieldId] : undefined;
}

function valuesToSelectionContext(values: Record<string, unknown>) {
  return buildSelectionContext({
    product: values[VASE_FLOW_FIELD_IDS.chosenProduct],
    finish: values[VASE_FLOW_FIELD_IDS.chosenFinish],
    colours: values[VASE_FLOW_FIELD_IDS.chosenColours],
    combination: values[VASE_FLOW_FIELD_IDS.chosenCombination],
  });
}

export function resolveSelectionContext(
  values: Record<string, unknown>,
  sourceValue?: unknown
): FlowSelectionContextValue | null {
  const source = asSelectionContext(sourceValue);
  const resolved = buildSelectionContext({
    product: source?.product ?? values[VASE_FLOW_FIELD_IDS.chosenProduct],
    finish: source?.finish ?? values[VASE_FLOW_FIELD_IDS.chosenFinish],
    colours: source?.colours ?? values[VASE_FLOW_FIELD_IDS.chosenColours],
    combination: source?.combination ?? values[VASE_FLOW_FIELD_IDS.chosenCombination],
  });

  if (resolved) {
    return {
      ...resolved,
      productId: source?.productId ?? resolved.productId,
    };
  }

  if (source?.productId != null) {
    return {
      product: null,
      productId: source.productId,
      finish: null,
      colours: [],
      combination: null,
    };
  }

  return null;
}

export function resolveItemContext(
  values: Record<string, unknown>,
  sourceValue?: unknown
): FlowItemContextValue | null {
  const context = asItemContext(sourceValue);
  if (context) return context;

  const requestItem = asRequestItem(sourceValue);
  if (!requestItem) return null;

  const selection = resolveSelectionContext(values, {
    product: requestItem.product,
    productId: requestItem.product?.id ?? null,
    finish: requestItem.finish,
    colours: requestItem.colours,
    combination: requestItem.combination,
  });

  return {
    selection,
    requestItem,
    productId: requestItem.product?.id ?? selection?.productId ?? null,
  };
}

function preserveSelectionProduct(
  current: FlowSelectionContextValue | null,
  previous: FlowSelectionContextValue | null
): FlowSelectionContextValue | null {
  if (!current) return current;
  if (current.product || current.productId != null) return current;
  if (!previous?.product && previous?.productId == null) return current;
  return {
    ...current,
    product: previous?.product ?? null,
    productId: previous?.product?.id ?? previous?.productId ?? null,
  };
}

function getSocketValue(
  flow: FlowDefinition,
  values: Record<string, unknown>,
  node: FlowNode,
  socketId: string,
  direction: 'input' | 'output' = 'input'
) {
  const rawValue = getRawSocketValue(flow, values, node, direction, socketId);
  const socketType =
    resolveFlowNodeSocketType(node, socketId, flow) ??
    (direction === 'input'
      ? getFlowNodeDefinition(node.blockType)?.sockets.find(
          (socket) => socket.direction === direction && socket.id === socketId
        )?.type
      : undefined) ??
    'scalar.string';

  if (socketType === 'context.selection') {
    return resolveSelectionContext(values, rawValue);
  }
  if (socketType === 'context.item') {
    return resolveItemContext(values, rawValue);
  }
  return rawValue;
}

function buildSelectionFromNodeContext(
  flow: FlowDefinition,
  values: Record<string, unknown>,
  node: FlowNode
) {
  const getNodeValue = (socketId: string) => {
    const outputValue = getSocketValue(flow, values, node, socketId, 'output');
    if (outputValue !== undefined) return outputValue;
    return getSocketValue(flow, values, node, socketId, 'input');
  };
  const incomingSelection = getSocketValue(flow, values, node, 'selection');
  const inputCount = getSelectionCollectorInputCount(node);
  const collectorSelections = Array.from({ length: inputCount }, (_, index) =>
    getSocketValue(flow, values, node, getSelectionCollectorInputSocketId(index + 1))
  );
  const base =
    collectorSelections
      .map((selection) => asSelectionContext(selection))
      .find((selection) => Boolean(selection)) ??
    asSelectionContext(incomingSelection);
  const combinationValue = getSocketValue(flow, values, node, 'combination');
  const selectedCombination =
    node.blockType === 'customiser-combination-results' &&
    combinationValue &&
    typeof combinationValue === 'object' &&
    !Array.isArray(combinationValue)
      ? (combinationValue as Record<string, unknown>)
      : null;
  const explicitCombination =
    combinationValue ??
    base?.combination ??
    values[VASE_FLOW_FIELD_IDS.chosenCombination];
  const combinationFinish =
    typeof selectedCombination?.finish_type === 'string' &&
    selectedCombination.finish_type.trim().length > 0
      ? selectedCombination.finish_type
      : null;
  const combinationColours = Array.isArray(selectedCombination?.colours)
    ? selectedCombination.colours.filter((entry): entry is string => typeof entry === 'string')
    : [];

  return resolveSelectionContext(values, {
    product:
      getNodeValue('product') ??
      base?.product ??
      values[VASE_FLOW_FIELD_IDS.chosenProduct],
    productId: base?.productId ?? null,
    finish:
      combinationFinish ??
      getNodeValue('finish') ??
      base?.finish ??
      values[VASE_FLOW_FIELD_IDS.chosenFinish],
    colours:
      (combinationColours.length > 0 ? combinationColours : null) ??
      getNodeValue('colours') ??
      base?.colours ??
      values[VASE_FLOW_FIELD_IDS.chosenColours],
    combination: explicitCombination,
  });
}

function buildPassThroughOutputValue(
  flow: FlowDefinition,
  values: Record<string, unknown>,
  node: FlowNode,
  socketId: string
) {
  switch (socketId) {
    case 'throughProduct':
      return getSocketValue(flow, values, node, 'product') ?? null;
    case 'throughFinish':
      return getSocketValue(flow, values, node, 'finish') ?? null;
    case 'throughColours':
      return getSocketValue(flow, values, node, 'colours') ?? [];
    case 'throughSelection':
      return asSelectionContext(getSocketValue(flow, values, node, 'selection'));
    default:
      return null;
  }
}

function applyPatchToFlowState(
  state: PageFlowState,
  flowId: string,
  patch: Record<string, unknown>
) {
  if (Object.keys(patch).length === 0) return state;
  const currentEntry = state[flowId] ?? { values: {}, updatedAt: new Date().toISOString() };
  return {
    ...state,
    [flowId]: {
      values: {
        ...currentEntry.values,
        ...patch,
      },
      updatedAt: new Date().toISOString(),
    },
  };
}

function applyValueToFlowField(
  state: PageFlowState,
  flowId: string,
  fieldId: string | null,
  nextValue: unknown
) {
  if (!fieldId) return state;
  const currentValues = getFlowEntryValues(state, flowId);
  if (valuesEqual(currentValues[fieldId], nextValue)) return state;
  return applyPatchToFlowState(state, flowId, {
    [fieldId]: nextValue,
  });
}

function buildCurrentRequestItemValue(values: Record<string, unknown>) {
  const existing = asRequestItem(values[REQUEST_FLOW_FIELD_IDS.currentRequestItem]);
  const built = buildRequestItemFromSelection({
    product: values[VASE_FLOW_FIELD_IDS.chosenProduct],
    finish: values[VASE_FLOW_FIELD_IDS.chosenFinish],
    colours: values[VASE_FLOW_FIELD_IDS.chosenColours],
    combination: values[VASE_FLOW_FIELD_IDS.chosenCombination],
    productName: values[VASE_FLOW_FIELD_IDS.productName],
    productPrice: values[VASE_FLOW_FIELD_IDS.productPrice],
    productMaterial: values[VASE_FLOW_FIELD_IDS.productMaterial],
  });
  if (!built) return undefined;
  const sameSelection =
    existing?.product?.id === built.product?.id &&
    existing?.finish === built.finish &&
    valuesEqual(existing?.colours ?? [], built.colours) &&
    (existing?.combination as { id?: string } | null)?.id ===
      (built.combination as { id?: string } | null)?.id;
  if (sameSelection && existing) {
    return {
      ...existing,
      productName: built.productName,
      productPrice: built.productPrice,
      productMaterial: built.productMaterial,
    };
  }
  return built;
}

function buildDerivedNodePatch(flow: FlowDefinition, values: Record<string, unknown>) {
  const patch: Record<string, unknown> = {};
  const workingValues: Record<string, unknown> = { ...values };
  const setPatchedValue = (fieldId: string, nextValue: unknown) => {
    if (!valuesEqual(workingValues[fieldId], nextValue)) {
      patch[fieldId] = nextValue;
      workingValues[fieldId] = nextValue;
    }
  };

  if (flow.fields.some((field) => field.id === REQUEST_FLOW_FIELD_IDS.currentRequestItem)) {
    const nextRequestItem = buildCurrentRequestItemValue(workingValues);
    setPatchedValue(REQUEST_FLOW_FIELD_IDS.currentRequestItem, nextRequestItem);
  }

  flow.nodes.forEach((node) => {
    switch (node.blockType) {
      case 'customiser-finish-picker':
      case 'customiser-colour-filter':
      case 'customiser-combination-results': {
        const selectionOutputFieldId = getSmartBindingFieldId(
          flow,
          node,
          'output',
          'updatedSelection'
        );
        if (selectionOutputFieldId) {
          setPatchedValue(
            selectionOutputFieldId,
            buildSelectionFromNodeContext(flow, workingValues, node)
          );
        }
        ['throughProduct', 'throughFinish', 'throughColours', 'throughSelection'].forEach(
          (socketId) => {
            const fieldId = getSmartBindingFieldId(flow, node, 'output', socketId);
            if (!fieldId) return;
            setPatchedValue(
              fieldId,
              buildPassThroughOutputValue(flow, workingValues, node, socketId)
            );
          }
        );
        break;
      }
      case 'build-item': {
        const outputFieldId = getSmartBindingFieldId(flow, node, 'output', 'item');
        if (!outputFieldId) return;
        const selection = asSelectionContext(
          getSocketValue(flow, workingValues, node, 'selection')
        );
        const nextValue = buildItemContextFromSelection(selection, {
          productName: workingValues[VASE_FLOW_FIELD_IDS.productName],
          productPrice: workingValues[VASE_FLOW_FIELD_IDS.productPrice],
          productMaterial: workingValues[VASE_FLOW_FIELD_IDS.productMaterial],
        });
        setPatchedValue(outputFieldId, nextValue);
        break;
      }
      case 'selection-collector': {
        const outputFieldId = getSmartBindingFieldId(flow, node, 'output', 'selection');
        const productOutputFieldId = getSmartBindingFieldId(flow, node, 'output', 'product');
        const itemsOutputFieldId = getSmartBindingFieldId(flow, node, 'output', 'items');
        const inputCount = getSelectionCollectorInputCount(node);
        const collectorMode = getSelectionCollectorMode(node);
        const selections = Array.from({ length: inputCount }, (_, index) => {
          const socketId = getSelectionCollectorInputSocketId(index + 1);
          return {
            socketId,
            current: asSelectionContext(getSocketValue(flow, workingValues, node, socketId)),
            previous: asSelectionContext(workingValues[getRuntimeMetaKey(node.id, socketId)]),
          };
        });
        const previousSource = workingValues[getRuntimeMetaKey(node.id, 'lastSource')];
        const previousItems = asRequestItemList(
          workingValues[getRuntimeMetaKey(node.id, 'collectedItems')]
        );
        const previousMode =
          typeof workingValues[getRuntimeMetaKey(node.id, 'lastCollectorMode')] === 'string'
            ? String(workingValues[getRuntimeMetaKey(node.id, 'lastCollectorMode')])
            : null;
        const previousSelection = asSelectionContext(
          workingValues[getRuntimeMetaKey(node.id, 'collectedSelection')]
        );
        const removeItemIdValue = getSocketValue(flow, workingValues, node, 'removeItemId');
        const currentRemoveItemId = resolveRemovedItemId(removeItemIdValue);
        const previousRemoveItemId = getRuntimeMetaString(
          workingValues,
          getRuntimeMetaKey(node.id, 'lastRemoveItemId')
        );

        let nextSource =
          typeof previousSource === 'string' && previousSource.length > 0
            ? previousSource
            : null;
        let nextSelection = previousSelection;
        let nextItems = previousItems;

        selections.forEach((selection) => {
          if (!valuesEqual(selection.previous, selection.current)) {
            nextSource = selection.socketId;
            if (selection.current) {
              nextSelection = preserveSelectionProduct(selection.current, nextSelection);
            }
          }
          setPatchedValue(getRuntimeMetaKey(node.id, selection.socketId), selection.current);
        });

        const changedSelections = selections.filter(
          (selection) => !valuesEqual(selection.previous, selection.current)
        );

        if (collectorMode === 'latest') {
          if (nextSelection) {
            const nextItem = buildRequestItemFromSelectionContext(nextSelection, {
              productName: workingValues[VASE_FLOW_FIELD_IDS.productName],
              productPrice: workingValues[VASE_FLOW_FIELD_IDS.productPrice],
              productMaterial: workingValues[VASE_FLOW_FIELD_IDS.productMaterial],
            });
            nextItems = nextItem ? [nextItem] : [];
          } else if (changedSelections.length > 0) {
            nextItems = [];
          }
        } else if (collectorMode === 'accumulate') {
          nextItems = changedSelections.reduce((list, selection) => {
            const nextItem = buildRequestItemFromSelectionContext(selection.current, {
              productName: workingValues[VASE_FLOW_FIELD_IDS.productName],
              productPrice: workingValues[VASE_FLOW_FIELD_IDS.productPrice],
              productMaterial: workingValues[VASE_FLOW_FIELD_IDS.productMaterial],
            });
            if (!nextItem) return list;
            const withoutExisting = removeRequestItem(list, nextItem);
            return withoutExisting.length !== list.length
              ? withoutExisting
              : appendRequestItem(list, nextItem);
          }, previousItems);
        } else {
          nextItems = changedSelections.reduce((list, selection) => {
            const nextItem = buildRequestItemFromSelectionContext(selection.current, {
              productName: workingValues[VASE_FLOW_FIELD_IDS.productName],
              productPrice: workingValues[VASE_FLOW_FIELD_IDS.productPrice],
              productMaterial: workingValues[VASE_FLOW_FIELD_IDS.productMaterial],
            });
            return nextItem ? appendRequestItem(list, nextItem) : list;
          }, previousItems);
        }

        if (collectorMode === 'latest' && previousMode !== 'latest' && nextItems.length > 1) {
          const lastItem = nextItems[nextItems.length - 1] ?? null;
          nextItems = lastItem ? [lastItem] : [];
          nextSelection = lastItem
            ? resolveSelectionContext(workingValues, {
                product: lastItem.product,
                productId: lastItem.product?.id ?? null,
                finish: lastItem.finish,
                colours: lastItem.colours,
                combination: lastItem.combination,
              })
            : null;
        }

        if (currentRemoveItemId != null && currentRemoveItemId !== previousRemoveItemId) {
          const removedAnything = nextItems.some((item) => item.id === currentRemoveItemId);
          const filteredItems = nextItems.filter((item) => item.id !== currentRemoveItemId);
          if (removedAnything) {
            nextItems = filteredItems;
          }
          if (
            removedAnything &&
            nextSelection?.productId != null &&
            !filteredItems.some((item) => item.product?.id === nextSelection.productId)
          ) {
            nextSelection = null;
            nextSource = null;
          }
        }

        setPatchedValue(getRuntimeMetaKey(node.id, 'lastSource'), nextSource);
        setPatchedValue(getRuntimeMetaKey(node.id, 'lastCollectorMode'), collectorMode);
        setPatchedValue(getRuntimeMetaKey(node.id, 'lastRemoveItemId'), currentRemoveItemId);
        setPatchedValue(getRuntimeMetaKey(node.id, 'collectedSelection'), nextSelection);
        setPatchedValue(getRuntimeMetaKey(node.id, 'collectedItems'), nextItems);
        if (outputFieldId) {
          setPatchedValue(outputFieldId, nextSelection);
        }
        if (productOutputFieldId) {
          setPatchedValue(productOutputFieldId, nextSelection?.product ?? null);
        }
        if (itemsOutputFieldId) {
          setPatchedValue(itemsOutputFieldId, nextItems);
        }
        break;
      }
      case 'add-to-list': {
        const listFieldId =
          getSmartBindingFieldId(flow, node, 'input', 'list') ??
          getSmartBindingFieldId(flow, node, 'output', 'updatedList');
        const outputListFieldId = getSmartBindingFieldId(flow, node, 'output', 'updatedList');
        const removeItemIdValue = getSocketValue(flow, workingValues, node, 'removeItemId');
        const currentRemoveItemId = resolveRemovedItemId(removeItemIdValue);
        const previousRemoveItemId = getRuntimeMetaString(
          workingValues,
          getRuntimeMetaKey(node.id, 'lastRemoveItemId')
        );

        if (currentRemoveItemId != null && currentRemoveItemId !== previousRemoveItemId) {
          const updatedList = removeRequestItemById(
            listFieldId ? workingValues[listFieldId] : getSocketValue(flow, workingValues, node, 'list'),
            currentRemoveItemId
          );
          if (listFieldId) {
            setPatchedValue(listFieldId, updatedList);
          }
          if (outputListFieldId) {
            setPatchedValue(outputListFieldId, updatedList);
          }
          syncCollectorStateToList(flow, workingValues, listFieldId ?? outputListFieldId, updatedList, patch);
        }

        setPatchedValue(getRuntimeMetaKey(node.id, 'lastRemoveItemId'), currentRemoveItemId);
        break;
      }
      case 'request-form': {
        break;
      }
      case 'source-selector': {
        const selectedFieldId =
          typeof node.config?.fieldId === 'string' ? node.config.fieldId : '';
        if (!selectedFieldId || !getSelectableSourceFieldIds().includes(selectedFieldId)) {
          return;
        }
        const outputFieldId = getBindingFieldId(node, 'output', 'value');
        if (!outputFieldId) return;
        let nextValue = workingValues[selectedFieldId];
        const fieldDefinition = flow.fields.find((field) => field.id === selectedFieldId) ?? null;
        if (fieldDefinition?.type === 'context.selection') {
          nextValue = resolveSelectionContext(workingValues, nextValue);
        } else if (fieldDefinition?.type === 'context.item') {
          nextValue = resolveItemContext(workingValues, nextValue);
        }
        setPatchedValue(outputFieldId, nextValue);
        break;
      }
      case 'compare': {
        const outputFieldId = getBindingFieldId(node, 'output', 'result');
        if (!outputFieldId) return;
        const valueType = (node.config?.valueType as any) ?? 'scalar.string';
        const operator = (node.config?.operator as CompareOperator) ?? 'equals';
        if (!getCompareOperatorsForValueType(valueType).includes(operator)) return;
        const nextValue = evaluateCompare({
          operator,
          leftValue: getSocketValue(flow, workingValues, node, 'leftValue'),
          rightValue: getSocketValue(flow, workingValues, node, 'rightValue'),
          valueType,
        });
        setPatchedValue(outputFieldId, nextValue);
        break;
      }
      case 'condition': {
        const outputFieldId = getBindingFieldId(node, 'output', 'result');
        if (!outputFieldId) return;
        const nextValue = evaluateCondition(
          getSocketValue(flow, workingValues, node, 'condition'),
          getSocketValue(flow, workingValues, node, 'trueValue'),
          getSocketValue(flow, workingValues, node, 'falseValue')
        );
        setPatchedValue(outputFieldId, nextValue);
        break;
      }
      default:
        break;
    }
  });

  return patch;
}

function evaluateGlobalPortalRuntime(flows: FlowDefinition[], state: PageFlowState) {
  let nextState = state;

  flows.forEach((flow) => {
    flow.nodes.forEach((node) => {
      if (node.blockType !== 'global-portal') return;

      const sourceValues = getFlowEntryValues(nextState, flow.id);
      const selection = asSelectionContext(getSocketValue(flow, sourceValues, node, 'selection'));
      const item = asItemContext(getSocketValue(flow, sourceValues, node, 'item'));
      const items = asRequestItemList(getSocketValue(flow, sourceValues, node, 'items'));
      const value = getSocketValue(flow, sourceValues, node, 'value');
      const receivers = findPortalReceiversForSender(flows, flow.id, node.id);
      receivers.forEach((receiver) => {
        const selectionFieldId = getBindingFieldId(receiver.node, 'output', 'selection');
        if (selectionFieldId) {
          nextState = applyValueToFlowField(
            nextState,
            receiver.flow.id,
            selectionFieldId,
            selection
          );
        }

        const itemFieldId = getBindingFieldId(receiver.node, 'output', 'item');
        if (itemFieldId) {
          nextState = applyValueToFlowField(nextState, receiver.flow.id, itemFieldId, item);
        }

        const itemsFieldId = getBindingFieldId(receiver.node, 'output', 'items');
        if (itemsFieldId) {
          nextState = applyValueToFlowField(nextState, receiver.flow.id, itemsFieldId, items);
        }

        const valueFieldId = getBindingFieldId(receiver.node, 'output', 'value');
        if (valueFieldId) {
          nextState = applyValueToFlowField(nextState, receiver.flow.id, valueFieldId, value);
        }
      });
    });
  });

  return nextState;
}

export function evaluateFlowRuntime(flows: FlowDefinition[], state: PageFlowState) {
  let nextState = state;

  flows.forEach((flow) => {
    const values = getFlowEntryValues(nextState, flow.id);
    const patch = buildDerivedNodePatch(flow, values);
    nextState = applyPatchToFlowState(nextState, flow.id, patch);
  });

  nextState = evaluateGlobalPortalRuntime(flows, nextState);

  flows.forEach((flow) => {
    const values = getFlowEntryValues(nextState, flow.id);
    const patch = buildDerivedNodePatch(flow, values);
    nextState = applyPatchToFlowState(nextState, flow.id, patch);
  });

  return nextState;
}

function invokeFlowNodeActionInternal(
  flows: FlowDefinition[],
  state: PageFlowState,
  flowId: string,
  nodeId: string,
  visited: Set<string>
) {
  const visitKey = `${flowId}:${nodeId}`;
  if (visited.has(visitKey)) return state;
  visited.add(visitKey);

  const flow = flows.find((entry) => entry.id === flowId);
  const node = flow?.nodes.find((entry) => entry.id === nodeId);
  const definition = getFlowNodeDefinition(node?.blockType);
  if (!flow || !node || !definition) return state;

  const values = getFlowEntryValues(state, flowId);
  let updatedList: RequestItem[] | null = null;

  if (node.blockType === 'global-portal') {
    const receivers = findPortalReceiversForSender(flows, flowId, node.id);
    return receivers.reduce((nextState, receiver) => {
      const triggerFieldId = getBindingFieldId(receiver.node, 'output', 'trigger');
      if (!triggerFieldId) return nextState;
      const connectedTargets = receiver.flow.nodes
        .filter((candidate) => candidate.id !== receiver.node.id)
        .filter((candidate) =>
          candidate.inputs.some((binding) => {
            if (binding.fieldId !== triggerFieldId) return false;
            const socketDefinition = resolveFlowNodeSockets(candidate, receiver.flow).find(
              (socket) => socket.direction === 'input' && socket.id === binding.socketId
            );
            return socketDefinition?.portKind === 'trigger';
          })
        );

      return connectedTargets.reduce(
        (targetState, targetNode) =>
          invokeFlowNodeActionInternal(
            flows,
            targetState,
            receiver.flow.id,
            targetNode.id,
            visited
          ),
        nextState
      );
    }, state);
  } else if (node.blockType === 'trigger-collector') {
    const triggerFieldId = getBindingFieldId(node, 'output', 'trigger');
    if (!triggerFieldId) return state;
    const connectedTargets = flow.nodes
      .filter((candidate) => candidate.id !== node.id)
      .filter((candidate) =>
        candidate.inputs.some((binding) => {
          if (binding.fieldId !== triggerFieldId) return false;
          const socketDefinition = resolveFlowNodeSockets(candidate, flow).find(
            (socket) => socket.direction === 'input' && socket.id === binding.socketId
          );
          return socketDefinition?.portKind === 'trigger';
        })
      );

    return connectedTargets.reduce(
      (targetState, targetNode) =>
        invokeFlowNodeActionInternal(flows, targetState, flow.id, targetNode.id, visited),
      state
    );
  } else if (node.blockType === 'add-to-list') {
    updatedList = Array.from({ length: getAddToListItemCount(node) }, (_, index) =>
      getSocketValue(flow, values, node, getAddToListItemSocketId(index + 1))
    ).reduce(
      (nextList, itemValue) => appendRequestItem(nextList, itemValue),
      getSocketValue(flow, values, node, 'list')
    );
  } else if (node.blockType === 'remove-from-list') {
    updatedList = removeRequestItem(
      getSocketValue(flow, values, node, 'list'),
      getSocketValue(flow, values, node, 'item')
    );
  } else if (node.blockType === 'clear-list') {
    updatedList = clearRequestItemList();
  } else if (node.blockType === 'request-form') {
    const requestItemsFieldId = resolveRequestItemsFieldId(flow, {
      nodeId: node.id,
      nodeBlockId: node.blockId ?? null,
    });
    if (!requestItemsFieldId) return state;
    const currentSelection = asSelectionContext(getSocketValue(flow, values, node, 'addSelection'));
    updatedList = clearRequestItemList();
    const patch: Record<string, unknown> = {
      [requestItemsFieldId]: updatedList,
      [getRuntimeMetaKey(node.id, 'lastDismissedSelectionProductId')]:
        currentSelection?.productId ?? null,
    };
    syncCollectorStateToList(flow, values, requestItemsFieldId, updatedList, patch);
    return applyPatchToFlowState(state, flowId, patch);
  } else if (node.blockType === 'set-step-tracker') {
    const stepId = typeof node.config?.stepId === 'string' ? node.config.stepId.trim() : '';
    const trackerId = typeof node.config?.trackerId === 'string' ? node.config.trackerId.trim() : '';
    return stepId ? setStepTrackerActiveStepInState(state, flows, stepId, trackerId || null) : state;
  }

  if (!updatedList) return state;

  const patch: Record<string, unknown> = {};
  const inputListFieldId = getSmartBindingFieldId(flow, node, 'input', 'list');
  const outputListFieldId = getSmartBindingFieldId(flow, node, 'output', 'updatedList');
  if (inputListFieldId) patch[inputListFieldId] = updatedList;
  if (outputListFieldId) patch[outputListFieldId] = updatedList;
  if (node.blockType === 'clear-list') {
    const requestItemsFieldId = inputListFieldId ?? outputListFieldId ?? null;
    if (requestItemsFieldId) {
      flow.nodes.forEach((candidate) => {
        if (candidate.blockType !== 'request-form') return;
        const candidateRequestItemsFieldId = resolveRequestItemsFieldId(flow, {
          nodeId: candidate.id,
          nodeBlockId: candidate.blockId ?? null,
        });
        if (candidateRequestItemsFieldId !== requestItemsFieldId) return;
        const currentSelection = asSelectionContext(
          getSocketValue(flow, values, candidate, 'addSelection')
        );
        patch[getRuntimeMetaKey(candidate.id, 'lastDismissedSelectionProductId')] =
          currentSelection?.productId ?? null;
      });
    }
  }
  if (node.blockType === 'clear-list' || node.blockType === 'remove-from-list') {
    const requestItemsFieldId = inputListFieldId ?? outputListFieldId ?? null;
    syncCollectorStateToList(flow, values, requestItemsFieldId, updatedList, patch);
  }

  return applyPatchToFlowState(state, flowId, patch);
}

export function invokeFlowNodeAction(
  flows: FlowDefinition[],
  state: PageFlowState,
  flowId: string,
  nodeId: string
) {
  return invokeFlowNodeActionInternal(flows, state, flowId, nodeId, new Set());
}

export function requestItemsToOrderItems(value: unknown): OrderItem[] {
  return asRequestItemList(value).map((item) => {
    const image =
      item.product?.id != null
        ? vases.find((vase) => vase.id === item.product?.id)?.image ?? ''
        : '';
    return {
      vaseId: item.product?.id ?? -1,
      vaseName: item.product?.name ?? item.productName ?? 'Unknown Vase',
      vaseImage: image,
      colorDescription:
        typeof (item.combination as { color_description?: unknown } | null)?.color_description ===
        'string'
          ? ((item.combination as { color_description: string }).color_description || 'Custom Combination')
          : 'Custom Combination',
      finishType: item.finish ?? 'shifting',
      colours: item.colours,
      quantity: 1,
      brandName:
        typeof (item.combination as { brand?: unknown } | null)?.brand === 'string'
          ? ((item.combination as { brand: string }).brand ?? null)
          : null,
      colorName:
        typeof (item.combination as { name?: unknown } | null)?.name === 'string'
          ? ((item.combination as { name: string }).name ?? null)
          : null,
      fid:
        typeof (item.combination as { fid?: unknown } | null)?.fid === 'string'
          ? ((item.combination as { fid: string }).fid ?? null)
          : null,
      blendSettings: undefined,
      calibrationSettings: undefined,
    };
  });
}

export function requestItemsToSelectedVases(value: unknown) {
  const seen = new Set<number>();
  return asRequestItemList(value).reduce<SelectedVase[]>((next, item) => {
    const product = item.product;
    if (!product || seen.has(product.id)) return next;
    seen.add(product.id);
    next.push(product);
    return next;
  }, []);
}
